import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaUploader } from "@/components/seller/MediaUploader";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const blank = { title: "", description: "", category: "", country: "Nigeria", price: "", discount_price: "", shipping_fee: "", stock: "", sku: "", images: [], videos: [], variants: [], flash_sale: false, product_type: "simple" };

export const ProductForm = ({ open, onOpenChange, product, cats = [], onSaved, sellCurrency = "EUR" }) => {
  const { rates = { EUR: 1 }, currencies = [] } = useCurrency() || {};
  const [f, setF] = useState(blank);
  const [curCode, setCurCode] = useState(sellCurrency);
  const [saving, setSaving] = useState(false);
  const editing = !!product;

  const rate = rates[curCode] || 1;                 // 1 EUR = `rate` curCode
  const curSymbol = currencies.find((c) => c.code === curCode)?.symbol || "€";
  const toEur = (v) => (parseFloat(v) || 0) / rate; // typed currency -> EUR base
  const fromEur = (eur) => {                          // EUR base -> typed currency (for edit display)
    const val = (parseFloat(eur) || 0) * rate;
    return curCode === "NGN" ? String(Math.round(val)) : String(Number(val.toFixed(2)));
  };

  useEffect(() => {
    if (open) {
      const startCur = sellCurrency || "EUR";
      setCurCode(startCur);
      const r = rates[startCur] || 1;
      const conv = (eur) => (startCur === "NGN" ? String(Math.round((parseFloat(eur) || 0) * r)) : String(Number(((parseFloat(eur) || 0) * r).toFixed(2))));
      if (product) {
        setF({
          title: product.title || "", description: product.description || "", category: product.category || (cats[0]?.slug || ""),
          country: product.country || "Nigeria", price: product.price != null ? conv(product.price) : "",
          discount_price: product.discount_price != null ? conv(product.discount_price) : "",
          shipping_fee: product.shipping_fee != null ? conv(product.shipping_fee) : "",
          stock: String(product.stock ?? ""), sku: product.sku || "",
          images: product.images || [], videos: product.videos || [], variants: (product.variants || []).map((v) => ({ ...v, price: v.price != null ? conv(v.price) : "" })),
          flash_sale: !!product.flash_sale,
          product_type: product.product_type || (product.variants?.length ? "variable" : "simple"),
        });
      } else {
        setF({ ...blank, category: cats[0]?.slug || "food-grocery" });
      }
    }
  }, [open, product, cats, sellCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addVariant = () => set("variants", [...f.variants, { name: "", price: "", stock: "" }]);
  const updVariant = (i, k, v) => set("variants", f.variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmVariant = (i) => set("variants", f.variants.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.price) return toast.error("Title and price are required");
    const payload = {
      title: f.title, description: f.description, category: f.category, country: f.country,
      price: Number(toEur(f.price).toFixed(2)), stock: parseInt(f.stock) || 0,
      discount_price: f.discount_price ? Number(toEur(f.discount_price).toFixed(2)) : null,
      shipping_fee: f.shipping_fee !== "" ? Number(toEur(f.shipping_fee).toFixed(2)) : null,
      sku: f.sku, images: f.images, videos: f.videos, flash_sale: f.flash_sale, product_type: f.product_type,
      variants: f.product_type === "variable" ? f.variants.filter((v) => v.name).map((v) => ({ name: v.name, price: v.price ? Number(toEur(v.price).toFixed(2)) : null, stock: v.stock ? parseInt(v.stock) : 0 })) : [],
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/products/${product.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product published — it's live now");
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription>{editing ? "Update your product details, media and variants." : "Add a new product with images, video and variants."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Product type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { v: "simple", label: "Simple", desc: "One price & stock" },
                { v: "variable", label: "Variable", desc: "Sizes, colours…" },
              ].map((o) => (
                <button key={o.v} type="button" data-testid={`prod-type-${o.v}`} onClick={() => set("product_type", o.v)}
                  className={`text-left p-2.5 rounded-xl border transition-colors ${f.product_type === o.v ? "border-primary bg-primary/5" : "hover:border-primary/50"}`}>
                  <p className="text-sm font-semibold">{o.label}</p>
                  <p className="text-[11px] text-muted-foreground">{o.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Title</Label><Input data-testid="prod-title" required value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div><Label className="text-xs">Description</Label><Textarea data-testid="prod-desc" value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="flex items-center justify-between gap-2 rounded-xl bg-muted/40 px-3 py-2">
            <Label className="text-xs">Prices entered in</Label>
            <Select value={curCode} onValueChange={setCurCode}>
              <SelectTrigger data-testid="prod-currency" className="h-8 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(currencies.length ? currencies : [{ code: "EUR", symbol: "€" }, { code: "USD", symbol: "$" }, { code: "NGN", symbol: "₦" }]).map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="prod-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Country</Label><Input data-testid="prod-country" value={f.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div>
              <Label className="text-xs">Price ({curSymbol})</Label>
              <Input data-testid="prod-price" type="number" step="0.01" required value={f.price} onChange={(e) => set("price", e.target.value)} />
              {curCode !== "EUR" && f.price !== "" && <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="prod-price-eur">≈ €{toEur(f.price).toFixed(2)} stored</p>}
            </div>
            <div><Label className="text-xs">Discount ({curSymbol}, optional)</Label><Input data-testid="prod-discount" type="number" step="0.01" value={f.discount_price} onChange={(e) => set("discount_price", e.target.value)} /></div>
            <div><Label className="text-xs">Stock</Label><Input data-testid="prod-stock" type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} /></div>
            <div><Label className="text-xs">SKU</Label><Input data-testid="prod-sku" value={f.sku} onChange={(e) => set("sku", e.target.value)} /></div>
            <div><Label className="text-xs">Shipping {curSymbol}/unit</Label><Input data-testid="prod-shipping" type="number" step="0.01" min="0" placeholder="Store default" value={f.shipping_fee} onChange={(e) => set("shipping_fee", e.target.value)} /></div>
          </div>

          <div>
            <Label className="text-xs">Media</Label>
            <MediaUploader images={f.images} videos={f.videos} onImages={(v) => set("images", v)} onVideos={(v) => set("videos", v)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs">Variants (size, colour…)</Label>
              <Button type="button" size="sm" variant="outline" data-testid="add-variant-btn" onClick={addVariant} className="h-7" disabled={f.product_type !== "variable"}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
            </div>
            {f.product_type !== "variable" ? (
              <p className="text-[11px] text-muted-foreground">Switch to a <strong>Variable</strong> product to add sizes/colours.</p>
            ) : (
            <div className="space-y-2">
              {f.variants.map((v, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`variant-row-${i}`}>
                  <Input placeholder="Name (e.g. Large / Red)" value={v.name} onChange={(e) => updVariant(i, "name", e.target.value)} className="flex-1" />
                  <Input placeholder={`±${curSymbol}`} type="number" step="0.01" value={v.price} onChange={(e) => updVariant(i, "price", e.target.value)} className="w-20" />
                  <Input placeholder="Qty" type="number" value={v.stock} onChange={(e) => updVariant(i, "stock", e.target.value)} className="w-16" />
                  <button type="button" onClick={() => rmVariant(i)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></button>
                </div>
              ))}
              {f.variants.length === 0 && <p className="text-[11px] text-muted-foreground">No variants — the base price and stock apply.</p>}
            </div>
            )}
          </div>

          <div className="flex items-center gap-2"><Switch data-testid="prod-flash" checked={f.flash_sale} onCheckedChange={(v) => set("flash_sale", v)} /><Label className="text-sm">Flash sale item</Label></div>
          <Button data-testid="save-product-btn" type="submit" disabled={saving} className="w-full rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Publish product"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
